import React, { useState, useEffect, useMemo, useCallback } from "react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { reportsAPI } from "../services/api";
import { fmtDate } from "../utils/date";
import { formatOptionSymbol } from "../utils/optionSymbol";

const C = {
  card: "#fff", border: "#e8edf3", text: "#0f172a", sub: "#64748b", muted: "#94a3b8",
  blue: "#2563eb", indigo: "#4f46e5", green: "#10b981", red: "#ef4444", amber: "#f59e0b", bg: "#f8fafc",
};
const card = (e = {}) => ({ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, ...e });
// Crypto portal reports are in USD (small $ amounts → 2 decimals, no Indian lakh);
// NSE reports in ₹ (whole rupees, lakh/k abbreviations). Host-based, like the rest.
const IS_CRYPTO_PORTAL = typeof window !== "undefined" && /^crypto\./i.test(window.location.hostname || "");
const CUR = IS_CRYPTO_PORTAL ? "$" : "₹";
const inr = (n) => IS_CRYPTO_PORTAL
  ? CUR + (Math.round((n || 0) * 100) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : CUR + Math.round(n || 0).toLocaleString("en-IN");
const inrK = (n) => {
  const a = Math.abs(n || 0), sign = n < 0 ? "-" : "";
  if (IS_CRYPTO_PORTAL) return sign + CUR + (a >= 1e6 ? (a / 1e6).toFixed(1) + "M" : a >= 1e3 ? (a / 1e3).toFixed(1) + "k" : String(Math.round(a * 100) / 100));
  return sign + CUR + (a >= 1e5 ? (a / 1e5).toFixed(1) + "L" : a >= 1e3 ? Math.round(a / 1e3) + "k" : Math.round(a));
};
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const sideOf = (instr) => { const t = String(instr || "").replace(/\s+/g, "").slice(-2).toUpperCase(); return t === "CE" ? "CE" : t === "PE" ? "PE" : null; };

function bucket(logs, keyFn) {
  const m = {};
  for (const t of logs) {
    const k = keyFn(t); if (k == null) continue;
    if (!m[k]) m[k] = { key: k, pnl: 0, n: 0, w: 0, l: 0 };
    m[k].pnl += t.pnl || 0; m[k].n++;
    if (t.status !== "OPEN") { if ((t.pnl || 0) > 0) m[k].w++; else m[k].l++; }
  }
  return m;
}

function computeAnalytics(report) {
  if (!report) return null;
  const logs = report.tradeLogs || [];
  const closed = logs.filter((t) => t.status !== "OPEN");
  const wins = closed.filter((t) => (t.pnl || 0) > 0);
  const losses = closed.filter((t) => (t.pnl || 0) <= 0);
  const grossProfit = wins.reduce((s, t) => s + (t.pnl || 0), 0);
  const grossLoss = Math.abs(losses.reduce((s, t) => s + (t.pnl || 0), 0));
  const winRate = closed.length ? (wins.length / closed.length) * 100 : null;
  const avgWin = wins.length ? grossProfit / wins.length : 0;
  const avgLoss = losses.length ? grossLoss / losses.length : 0;
  const profitFactor = grossLoss ? grossProfit / grossLoss : (grossProfit ? Infinity : 0);
  const expectancy = closed.length ? closed.reduce((s, t) => s + (t.pnl || 0), 0) / closed.length : 0;

  // daily equity from the backend calendar
  const cal = report.calendar || {};
  const days = Object.keys(cal).sort();
  let cum = 0, peak = 0, maxDD = 0;
  const equity = days.map((d) => { cum += cal[d]; if (cum > peak) peak = cum; const dd = cum - peak; if (dd < maxDD) maxDD = dd; return { date: d, label: d.slice(5), pnl: Math.round(cal[d]), cum: Math.round(cum) }; });
  const bestDay = days.length ? days.reduce((b, d) => (cal[d] > cal[b] ? d : b), days[0]) : null;
  const worstDay = days.length ? days.reduce((b, d) => (cal[d] < cal[b] ? d : b), days[0]) : null;
  const greenDays = days.filter((d) => cal[d] > 0).length;
  const redDays = days.filter((d) => cal[d] < 0).length;

  // streaks
  const ordered = [...closed].sort((a, b) => new Date(a.exitTime || a.date) - new Date(b.exitTime || b.date));
  let winStreak = 0, lossStreak = 0, cw = 0, cl = 0;
  for (const t of ordered) { if ((t.pnl || 0) > 0) { cw++; cl = 0; if (cw > winStreak) winStreak = cw; } else { cl++; cw = 0; if (cl > lossStreak) lossStreak = cl; } }

  // avg duration (minutes)
  const durs = closed.filter((t) => t.exitTime && t.date).map((t) => (new Date(t.exitTime) - new Date(t.date)) / 60000).filter((x) => x >= 0);
  const avgDurMin = durs.length ? durs.reduce((s, x) => s + x, 0) / durs.length : 0;

  // breakdowns
  const byStrategy = (report.breakdown || []).slice().sort((a, b) => b.pnl - a.pnl);
  const bySide = bucket(logs, (t) => sideOf(t.instrument));
  const byProduct = bucket(logs, (t) => t.product || "MIS");
  const byExit = bucket(closed, (t) => t.exitReason || "AUTO");
  const byHour = Object.values(bucket(logs, (t) => { const h = new Date(t.entryTime || t.date).getHours(); return h >= 9 && h <= 15 ? String(h).padStart(2, "0") : null; })).sort((a, b) => a.key.localeCompare(b.key)).map((b) => ({ ...b, label: b.key + ":00" }));
  const byDow = Object.values(bucket(logs, (t) => String(new Date(t.date).getDay()))).sort((a, b) => Number(a.key) - Number(b.key)).map((b) => ({ ...b, label: DOW[Number(b.key)] }));

  // insights
  const ins = [];
  if (byStrategy.length) { const top = byStrategy[0], bot = byStrategy[byStrategy.length - 1];
    ins.push({ icon: "🏆", text: `Best strategy: ${top.strategy} (${inr(top.pnl)})${byStrategy.length > 1 && bot.pnl < 0 ? ` · weakest: ${bot.strategy} (${inr(bot.pnl)})` : ""}` }); }
  if (isFinite(profitFactor) && closed.length) ins.push({ icon: profitFactor >= 1 ? "📈" : "📉", text: `Profit factor ${profitFactor.toFixed(2)} — har ${CUR}1 loss ke against ${CUR}${profitFactor.toFixed(2)} profit. Expectancy ${inr(expectancy)}/trade.` });
  if (bySide.CE && bySide.PE) { const b = bySide.CE.pnl >= bySide.PE.pnl ? "CE" : "PE", w = bySide.CE.pnl >= bySide.PE.pnl ? "PE" : "CE";
    ins.push({ icon: "⚖️", text: `${b} trades outperform ${w} (${inr(bySide[b].pnl)} vs ${inr(bySide[w].pnl)}).` }); }
  if (byExit.MANUAL && byExit.MANUAL.n) ins.push({ icon: "🖐", text: `${byExit.MANUAL.n} manual exits → ${inr(byExit.MANUAL.pnl)}. ${byExit.MANUAL.pnl < 0 ? "Manual exits net negative — discipline check." : "Manual exits net positive."}` });
  if (byHour.length) { const best = byHour.reduce((a, b) => (b.pnl > a.pnl ? b : a)); const worst = byHour.reduce((a, b) => (b.pnl < a.pnl ? b : a));
    ins.push({ icon: "⏰", text: `Best entry window ${best.label} (${inr(best.pnl)})${worst.key !== best.key ? ` · worst ${worst.label} (${inr(worst.pnl)})` : ""}.` }); }
  if (winStreak || lossStreak) ins.push({ icon: "🔥", text: `Longest win streak ${winStreak} · longest loss streak ${lossStreak}.` });
  if (maxDD < 0) ins.push({ icon: "🛡", text: `Max drawdown ${inr(maxDD)}. ${greenDays} green days vs ${redDays} red days.` });

  return { closed, wins, losses, grossProfit, grossLoss, winRate, avgWin, avgLoss, profitFactor, expectancy,
    equity, bestDay, worstDay, greenDays, redDays, winStreak, lossStreak, avgDurMin, maxDD,
    byStrategy, bySide, byProduct, byExit, byHour, byDow, insights: ins };
}

// ── P&L calendar heatmap (month grids) ──────────────────────────────────────
function Heatmap({ calendar }) {
  const months = useMemo(() => {
    const days = Object.keys(calendar || {});
    if (!days.length) return [];
    const byMonth = {};
    for (const d of days) { const ym = d.slice(0, 7); (byMonth[ym] = byMonth[ym] || {})[d] = calendar[d]; }
    const maxAbs = Math.max(1, ...days.map((d) => Math.abs(calendar[d])));
    return Object.keys(byMonth).sort().map((ym) => ({ ym, days: byMonth[ym], maxAbs }));
  }, [calendar]);
  if (!months.length) return null;
  const cellBg = (v, maxAbs) => {
    if (v == null) return "#f1f5f9";
    const a = Math.min(1, Math.abs(v) / maxAbs) * 0.85 + 0.15;
    return v >= 0 ? `rgba(16,185,129,${a})` : `rgba(239,68,68,${a})`;
  };
  return (
    <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
      {months.map(({ ym, days, maxAbs }) => {
        const [y, m] = ym.split("-").map(Number);
        const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
        const total = new Date(Date.UTC(y, m, 0)).getUTCDate();
        const cells = [];
        for (let i = 0; i < first; i++) cells.push(null);
        for (let dnum = 1; dnum <= total; dnum++) cells.push(`${ym}-${String(dnum).padStart(2, "0")}`);
        const monthPnl = Object.values(days).reduce((s, v) => s + v, 0);
        return (
          <div key={ym} style={{ ...card({ padding: 12 }), minWidth: 232 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <b style={{ fontSize: 13, color: C.text }}>{MON[m - 1]} {y}</b>
              <span style={{ fontSize: 12, fontWeight: 700, color: monthPnl >= 0 ? C.green : C.red }}>{inr(monthPnl)}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3 }}>
              {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} style={{ textAlign: "center", fontSize: 9, color: C.muted, fontWeight: 700 }}>{d}</div>)}
              {cells.map((d, i) => {
                const v = d ? days[d] : null;
                return (
                  <div key={i} title={d ? `${d}: ${v != null ? inr(v) : "no trades"}` : ""}
                    style={{ aspectRatio: "1", borderRadius: 4, background: d ? (v != null ? cellBg(v, maxAbs) : "#f1f5f9") : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9.5,
                      color: v != null ? "#fff" : C.muted, fontWeight: v != null ? 700 : 400 }}>
                    {d ? Number(d.slice(-2)) : ""}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const Stat = ({ label, value, color, sub }) => (
  <div style={{ ...card({ padding: 16 }), textAlign: "center" }}>
    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".3px" }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 900, color: color || C.text }}>{value}</div>
    {sub ? <div style={{ fontSize: 11, color: C.sub, marginTop: 3 }}>{sub}</div> : null}
  </div>
);

const Section = ({ title, right, children }) => (
  <div style={{ marginTop: 22 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
      <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, color: C.text }}>{title}</h2>
      {right}
    </div>
    {children}
  </div>
);

const TT = ({ active, payload, label }) => active && payload?.length ? (
  <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,.08)" }}>
    <div style={{ color: C.sub }}>{label}</div>
    {payload.map((p, i) => <div key={i} style={{ fontWeight: 700, color: p.color }}>{inr(p.value)}</div>)}
  </div>
) : null;

const MiniBreak = ({ title, rows }) => (
  <div style={card({ padding: 16 })}>
    <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, marginBottom: 10 }}>{title}</div>
    {rows.length ? rows.map((r) => (
      <div key={r.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${C.bg}` }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{r.label || r.key}</span>
        <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: C.muted }}>{r.n} tr{r.w + r.l > 0 ? ` · ${Math.round(r.w / (r.w + r.l) * 100)}%` : ""}</span>
          <b style={{ fontSize: 13, color: r.pnl >= 0 ? C.green : C.red, minWidth: 70, textAlign: "right" }}>{inr(r.pnl)}</b>
        </span>
      </div>
    )) : <div style={{ fontSize: 12, color: C.muted }}>No data</div>}
  </div>
);

export default function ReportsView() {
  const [from, setFrom] = useState("2025-01-01");
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState("all");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ k: "date", dir: "desc" });

  const fetchReport = useCallback(async (m, f, t) => {
    setLoading(true);
    try { const r = await reportsAPI.get(f ?? from, t ?? to, m ?? mode); setReport(r.data); }
    catch { setReport(null); } finally { setLoading(false); }
  }, [from, to, mode]);

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, []);

  const A = useMemo(() => computeAnalytics(report), [report]);
  const totalTrades = report?.breakdown?.reduce((s, b) => s + b.totalTrades, 0) || 0;
  const openCount = report?.breakdown?.reduce((s, b) => s + (b.open || 0), 0) || 0;

  const rows = useMemo(() => {
    let r = (report?.tradeLogs || []).slice();
    if (q.trim()) { const s = q.trim().toLowerCase(); r = r.filter((t) => (t.strategy + " " + t.instrument + " " + (t.entryReason || "") + " " + (t.exitNote || "")).toLowerCase().includes(s)); }
    const dir = sort.dir === "asc" ? 1 : -1;
    r.sort((a, b) => {
      const va = sort.k === "date" ? new Date(a.date) : sort.k === "pnl" ? (a.pnl || 0) : String(a[sort.k] || "");
      const vb = sort.k === "date" ? new Date(b.date) : sort.k === "pnl" ? (b.pnl || 0) : String(b[sort.k] || "");
      return va < vb ? -dir : va > vb ? dir : 0;
    });
    return r;
  }, [report, q, sort]);

  const exportCSV = () => {
    const head = ["Date", "Strategy", "Instrument", "Action", "Qty", "Entry", "Exit", "P&L", "Product", "Status", "ExitType", "EntryReason", "ExitNote"];
    const lines = [head.join(",")];
    for (const t of rows) lines.push([fmtDate(t.date), t.strategy, formatOptionSymbol(t.instrument), t.action, t.qty, t.price ?? "", t.exitPrice ?? "", Math.round(t.pnl || 0), t.product || "", t.status, t.exitReason || "", `"${(t.entryReason || "").replace(/"/g, "'")}"`, `"${(t.exitNote || "").replace(/"/g, "'")}"`].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `report_${from}_${to}.csv`; a.click();
  };

  const setSortK = (k) => setSort((s) => ({ k, dir: s.k === k && s.dir === "desc" ? "asc" : "desc" }));
  const arrow = (k) => sort.k === k ? (sort.dir === "desc" ? " ▾" : " ▴") : "";
  const inp = { padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 9, fontSize: 13, outline: "none", background: C.bg };

  return (
    <div>
      <div className="page-hero" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <div className="hero-icon" style={{ width: 46, height: 46, borderRadius: 13, background: "linear-gradient(135deg,#4f46e5,#2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23, boxShadow: "0 6px 16px rgba(37,99,235,.28)" }}>📊</div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: "-0.3px", color: C.text }}>Reports & Analytics</h1>
      </div>

      {/* filters */}
      <div style={{ ...card({ padding: 16 }), display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 18 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>FROM</label>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inp} />
        <label style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>TO</label>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inp} />
        <button onClick={() => fetchReport()} disabled={loading} style={{ padding: "10px 22px", background: "linear-gradient(135deg,#4f46e5,#2563eb)", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>{loading ? "Loading…" : "Apply"}</button>
        <div style={{ display: "inline-flex", background: "#eef1f6", borderRadius: 11, padding: 3, gap: 3, marginLeft: "auto" }}>
          {[["all", "All"], ["live", "Live"], ["paper", "Paper"]].map(([m, l]) => (
            <button key={m} onClick={() => { setMode(m); fetchReport(m); }} style={{ padding: "7px 16px", fontSize: 12.5, fontWeight: 700, borderRadius: 8, border: "none", cursor: "pointer", background: mode === m ? "#fff" : "transparent", color: mode === m ? (m === "paper" ? "#7c3aed" : m === "live" ? C.green : C.blue) : C.sub, boxShadow: mode === m ? "0 1px 3px rgba(15,23,42,.14)" : "none" }}>{l}</button>
          ))}
        </div>
      </div>

      {!report ? <div style={{ ...card({ padding: 60 }), textAlign: "center", color: C.muted }}>{loading ? "Loading reports…" : "No data"}</div> : totalTrades === 0 ? (
        <div style={{ ...card({ padding: 60 }), textAlign: "center", color: C.muted }}><div style={{ fontSize: 44, marginBottom: 10 }}>📭</div><div style={{ fontWeight: 700, fontSize: 16, color: C.sub }}>No trades in this range</div></div>
      ) : (
        <>
          {/* headline stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14, marginBottom: 6 }}>
            <Stat label="Net P&L" value={inr(report.totalPnl)} color={report.totalPnl >= 0 ? C.green : C.red} sub={mode === "all" ? `Live ${inrK(report.livePnl)} · Paper ${inrK(report.paperPnl)}` : null} />
            <Stat label="Win Rate" value={A.winRate == null ? "—" : Math.round(A.winRate) + "%"} sub={`${A.wins.length}W / ${A.losses.length}L`} />
            <Stat label="Profit Factor" value={isFinite(A.profitFactor) ? A.profitFactor.toFixed(2) : "∞"} color={A.profitFactor >= 1 ? C.green : C.red} sub={`Expectancy ${inrK(A.expectancy)}`} />
            <Stat label="Avg Win / Loss" value={`${inrK(A.avgWin)} / ${inrK(-A.avgLoss)}`} sub={`R:R ${A.avgLoss ? (A.avgWin / A.avgLoss).toFixed(2) : "—"}`} />
            <Stat label="Max Drawdown" value={inr(A.maxDD)} color={C.red} sub={`${A.greenDays} green · ${A.redDays} red days`} />
            <Stat label="Trades" value={totalTrades} sub={`${openCount} open · ${A.winStreak}🔥 win streak`} />
          </div>

          {/* insights */}
          {A.insights.length > 0 && (
            <Section title="💡 Insights">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
                {A.insights.map((ins, i) => (
                  <div key={i} style={{ ...card({ padding: "12px 14px" }), display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: C.text, lineHeight: 1.4 }}>
                    <span style={{ fontSize: 16 }}>{ins.icon}</span><span>{ins.text}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* equity curve */}
          {A.equity.length > 0 && (
            <Section title="Equity Curve (cumulative P&L)">
              <div style={card({ padding: 16 })}>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={A.equity} margin={{ top: 6, right: 12, left: 4, bottom: 0 }}>
                    <defs><linearGradient id="eq" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.blue} stopOpacity={0.3} /><stop offset="100%" stopColor={C.blue} stopOpacity={0.02} /></linearGradient></defs>
                    <CartesianGrid stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.sub }} minTickGap={24} />
                    <YAxis tick={{ fontSize: 11, fill: C.sub }} tickFormatter={inrK} width={54} />
                    <Tooltip content={<TT />} />
                    <Area type="monotone" dataKey="cum" stroke={C.blue} strokeWidth={2} fill="url(#eq)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Section>
          )}

          {/* daily P&L */}
          {A.equity.length > 0 && (
            <Section title="Daily P&L">
              <div style={card({ padding: 16 })}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={A.equity} margin={{ top: 6, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.sub }} minTickGap={24} />
                    <YAxis tick={{ fontSize: 11, fill: C.sub }} tickFormatter={inrK} width={54} />
                    <Tooltip content={<TT />} />
                    <Bar dataKey="pnl" radius={[3, 3, 0, 0]}>
                      {A.equity.map((d, i) => <Cell key={i} fill={d.pnl >= 0 ? C.green : C.red} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
          )}

          {/* calendar heatmap */}
          <Section title="P&L Calendar"><Heatmap calendar={report.calendar} /></Section>

          {/* breakdowns */}
          <Section title="Breakdowns">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
              <MiniBreak title="By Strategy" rows={A.byStrategy.map((b) => ({ key: b.strategy, label: b.strategy, pnl: b.pnl, n: b.totalTrades, w: b.wins, l: b.losses }))} />
              <MiniBreak title="Call vs Put" rows={Object.values(A.bySide)} />
              {!IS_CRYPTO_PORTAL && <MiniBreak title="MIS vs NRML" rows={Object.values(A.byProduct)} />}
              <MiniBreak title="Auto vs Manual exit" rows={Object.values(A.byExit)} />
            </div>
          </Section>

          {/* time analysis */}
          {(A.byHour.length > 0 || A.byDow.length > 0) && (
            <Section title="Time Analysis">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
                {A.byHour.length > 0 && (
                  <div style={card({ padding: 16 })}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, marginBottom: 8 }}>P&L by entry hour</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={A.byHour} margin={{ top: 4, right: 8, left: 4, bottom: 0 }}>
                        <CartesianGrid stroke="#eef2f7" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.sub }} />
                        <YAxis tick={{ fontSize: 11, fill: C.sub }} tickFormatter={inrK} width={50} />
                        <Tooltip content={<TT />} />
                        <Bar dataKey="pnl" radius={[3, 3, 0, 0]}>{A.byHour.map((d, i) => <Cell key={i} fill={d.pnl >= 0 ? C.green : C.red} />)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {A.byDow.length > 0 && (
                  <div style={card({ padding: 16 })}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, marginBottom: 8 }}>P&L by weekday</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={A.byDow} margin={{ top: 4, right: 8, left: 4, bottom: 0 }}>
                        <CartesianGrid stroke="#eef2f7" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.sub }} />
                        <YAxis tick={{ fontSize: 11, fill: C.sub }} tickFormatter={inrK} width={50} />
                        <Tooltip content={<TT />} />
                        <Bar dataKey="pnl" radius={[3, 3, 0, 0]}>{A.byDow.map((d, i) => <Cell key={i} fill={d.pnl >= 0 ? C.green : C.red} />)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* strategy table */}
          {report.breakdown?.length > 0 && (
            <Section title="Strategy Breakdown">
              <div className="ar-tablewrap" style={{ ...card({ padding: 0 }), overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ background: C.bg }}>{["Strategy", "Trades", "Wins", "Losses", "Open", "Win%", "P&L"].map((h) => <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 11.5, color: C.muted, fontWeight: 700 }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {A.byStrategy.map((b, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${C.bg}` }}>
                        <td style={{ padding: "12px 16px", fontWeight: 700 }}>{b.strategy}{b.paper && <span style={{ marginLeft: 8, padding: "2px 7px", borderRadius: 20, background: "#f5f3ff", color: "#7c3aed", fontSize: 10, fontWeight: 800 }}>PAPER</span>}</td>
                        <td style={{ padding: "12px 16px" }}>{b.totalTrades}</td>
                        <td style={{ padding: "12px 16px", color: C.green, fontWeight: 700 }}>{b.wins}</td>
                        <td style={{ padding: "12px 16px", color: C.red, fontWeight: 700 }}>{b.losses}</td>
                        <td style={{ padding: "12px 16px", color: C.blue, fontWeight: 700 }}>{b.open || 0}</td>
                        <td style={{ padding: "12px 16px" }}>{(b.wins + b.losses) > 0 ? Math.round(b.wins / (b.wins + b.losses) * 100) + "%" : "—"}</td>
                        <td style={{ padding: "12px 16px", fontWeight: 800, color: b.pnl >= 0 ? C.green : C.red }}>{inr(b.pnl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* trade log */}
          {report.tradeLogs?.length > 0 && (
            <Section title="Trade Log" right={
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" style={{ ...inp, padding: "7px 11px", maxWidth: "100%" }} />
                <button onClick={exportCSV} style={{ padding: "7px 14px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontWeight: 700, fontSize: 12.5, cursor: "pointer", color: C.text }}>⬇ CSV</button>
              </div>
            }>
              <div className="ar-tablewrap" style={{ ...card({ padding: 0 }), overflow: "auto", maxHeight: 560 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead><tr style={{ background: C.bg, position: "sticky", top: 0 }}>
                    {[["date", "Date"], ["strategy", "Strategy"], ["instrument", "Instrument"], ["action", "Action"], ["qty", "Qty"], ["pnl", "P&L"], [null, "Exit"]].map(([k, h]) => (
                      <th key={h} onClick={() => k && setSortK(k)} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11.5, color: C.muted, fontWeight: 700, cursor: k ? "pointer" : "default", whiteSpace: "nowrap" }}>{h}{k ? arrow(k) : ""}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {rows.map((t) => {
                      const open = t.status === "OPEN", manual = t.exitReason === "MANUAL";
                      const badge = open ? { l: t.live ? "🟢 Open" : "Open", bg: "#dbeafe", c: "#1e40af" } : manual ? { l: "🖐 Manual", bg: "#fef3c7", c: "#92400e" } : { l: "Auto", bg: "#f3f4f6", c: "#6b7280" };
                      return (
                        <tr key={t.id} style={{ borderTop: `1px solid ${C.bg}` }}>
                          <td style={{ padding: "10px 14px", color: C.sub, whiteSpace: "nowrap" }}>{fmtDate(t.date)}</td>
                          <td style={{ padding: "10px 14px", fontWeight: 700 }}>{t.strategy}{t.paper && <span style={{ marginLeft: 5, padding: "1px 6px", borderRadius: 20, background: "#f5f3ff", color: "#7c3aed", fontSize: 9, fontWeight: 800 }}>P</span>}</td>
                          <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12 }}>{formatOptionSymbol(t.instrument)}</td>
                          <td style={{ padding: "10px 14px", fontWeight: 700, color: t.action === "BUY" ? C.green : C.red }}>{t.action}</td>
                          <td style={{ padding: "10px 14px" }}>{t.qty}</td>
                          <td style={{ padding: "10px 14px", fontWeight: 700, color: (t.pnl || 0) >= 0 ? C.green : C.red }}>{inr(t.pnl)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{ padding: "3px 9px", borderRadius: 20, background: badge.bg, color: badge.c, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }}>{badge.l}</span>
                            {!open && t.exitNote ? <div style={{ marginTop: 3, fontSize: 10.5, color: C.muted, maxWidth: 220 }}>{t.exitNote}</div> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
